import type { OrderLeg } from '../services/orders.service';
import { parseMdYDate } from './dateUtils';

// Location recnos that represent GWI grounding yards. A leg cannot use a
// STOPOFF type at one of these locations (they're terminate/originate points).
export const GWI_YARD_RECNOS = [28804, 34544]; // GWI22, GWI26

const STAMP_FIELDS = [
  'origArrivalDateTime',
  'origDepartDateTime',
  'destArrivalDateTime',
  'destDepartDateTime',
] as const;

// A stamp is set unless blank or a MySQL zero-date sentinel.
const hasStamp = (v: unknown): boolean => {
  const s = String(v ?? '').trim();
  return s !== '' && !s.startsWith('0000-00-00');
};

type ValidateLegParams = {
  legs: OrderLeg[];
  editedLegs: Record<number, Partial<OrderLeg>>;
  // OTR orders skip the no-driver-gaps rule (over-the-road moves don't have
  // the same continuity expectation as multi-leg dray).
  otr: boolean;
};

// Validate the leg-level changes for an order. Returns user-facing error
// strings; empty array = passing. Operates on effective values (per-leg edits
// override the underlying leg data). Does NOT validate order-level fields like
// chassis type — those are caller's responsibility.
export const validateLegChanges = ({ legs, editedLegs, otr }: ValidateLegParams): string[] => {
  const errors: string[] = [];

  const getEffective = <K extends keyof OrderLeg>(leg: OrderLeg, field: K): OrderLeg[K] => {
    const edits = editedLegs[leg.legrecno];
    if (edits && field in edits) return edits[field] as OrderLeg[K];
    return leg[field];
  };

  // --- Per-leg validations ---
  let lastLegWithDriver = -1;
  legs.forEach((leg, index) => {
    const displayLegnum = leg.legnum;
    const currentDriver = String(getEffective(leg, 'driver') || '').trim();
    const currentOrigAppt = String(getEffective(leg, 'origApptDate') || '').trim();
    const currentDestAppt = String(getEffective(leg, 'destApptDate') || '').trim();
    const currentOrigcode = getEffective(leg, 'origcode');
    const currentDestcode = getEffective(leg, 'destcode');
    const currentLegtype = String(getEffective(leg, 'legtype') || '');
    const currentLegAction2 = String(getEffective(leg, 'legAction2') || '');

    // Rule: driver requires both locations
    if (currentDriver) {
      if (!currentOrigcode) {
        errors.push(`Leg ${displayLegnum}: Origin location is required when driver is assigned`);
      }
      if (!currentDestcode) {
        errors.push(`Leg ${displayLegnum}: Destination location is required when driver is assigned`);
      }
    }

    // Rule: driver requires both appointment dates
    if (currentDriver) {
      if (!currentOrigAppt) {
        errors.push(`Leg ${displayLegnum}: Origin appointment date is required when driver is assigned`);
      }
      if (!currentDestAppt) {
        errors.push(`Leg ${displayLegnum}: Destination appointment date is required when driver is assigned`);
      }
    }

    // Rule: an arrival or depart time requires a driver (v1 cms.js:5153 — lost in
    // the port, bugfix/#34227). The four manual stamps only, as v1; geofence stamps
    // are only ever written onto a leg that has a driver. Server twin:
    // leg_driver_rules.py.
    if (!currentDriver && STAMP_FIELDS.some((f) => hasStamp(getEffective(leg, f)))) {
      errors.push(`Leg ${displayLegnum}: A driver is required when an arrival or depart time is entered`);
    }

    // Rule: GWI yard locations cannot have STOPOFF legtype/legAction2
    if (currentOrigcode && GWI_YARD_RECNOS.includes(Number(currentOrigcode)) && currentLegtype === 'STOPOFF') {
      errors.push(`Leg ${displayLegnum}: Origin location is a grounding yard and cannot have a STOPOFF leg type`);
    }
    if (currentDestcode && GWI_YARD_RECNOS.includes(Number(currentDestcode)) && currentLegAction2 === 'STOPOFF') {
      errors.push(`Leg ${displayLegnum}: Destination location is a grounding yard and cannot have a STOPOFF leg action`);
    }

    // Rule: no driver gaps — cannot assign a driver if any earlier leg has no driver (non-OTR only)
    if (!otr && currentDriver) {
      if (lastLegWithDriver < index - 1) {
        for (let i = lastLegWithDriver + 1; i < index; i++) {
          const prevLeg = legs[i];
          const prevDriver = String(getEffective(prevLeg, 'driver') || '').trim();
          if (!prevDriver) {
            errors.push(`Leg ${displayLegnum}: Cannot assign a driver when Leg ${prevLeg.legnum} does not have a driver`);
            break;
          }
        }
      }
      lastLegWithDriver = index;
    }

    // Rule: origApptDate <= destApptDate within the same leg
    if (currentOrigAppt && currentDestAppt) {
      const origDate = parseMdYDate(currentOrigAppt);
      const destDate = parseMdYDate(currentDestAppt);
      if (origDate && destDate && origDate > destDate) {
        errors.push(`Leg ${displayLegnum}: Origin appointment date cannot be after destination appointment date`);
      }
    }

    // Rule: origApptDate >= previous leg's destApptDate
    if (index > 0 && currentOrigAppt) {
      const prevLeg = legs[index - 1];
      const prevDestAppt = String(getEffective(prevLeg, 'destApptDate') || '').trim();
      if (prevDestAppt) {
        const origDate = parseMdYDate(currentOrigAppt);
        const prevDestDate = parseMdYDate(prevDestAppt);
        if (origDate && prevDestDate && origDate < prevDestDate) {
          errors.push(`Leg ${displayLegnum}: Origin appointment date cannot be before Leg ${prevLeg.legnum} destination appointment date`);
        }
      }
    }
  });

  // --- Stop-type uniqueness validations ---
  // An N-leg chain has N+1 distinct stops: the first leg's origin (legtype)
  // and each leg's destination (legAction2). legtype on legs 2..N is the
  // cascade mirror of the previous leg's legAction2 — the same physical
  // stop, so it does not contribute a separate count. Without this, a chain
  // like leg1{PICKUP→DELIVERY} + leg2{DELIVERY→PICKUP} would pass with a
  // simple legtype/legAction2 count even though it has two pickups.
  const countStopsOfType = (type: string): number => {
    if (legs.length === 0) return 0;
    const firstLegType = String(getEffective(legs[0], 'legtype') || '');
    const startCount = firstLegType === type ? 1 : 0;
    const destCount = legs.filter(
      (l) => String(getEffective(l, 'legAction2') || '') === type,
    ).length;
    return startCount + destCount;
  };

  const pickupCount = countStopsOfType('PICKUP');
  if (pickupCount > 1) {
    errors.push(`Only one PICKUP is allowed per order (found ${pickupCount})`);
  }
  if (pickupCount === 0) {
    errors.push('Order must have at least one PICKUP stop');
  }
  const deliveryCount = countStopsOfType('DELIVERY');
  if (deliveryCount > 1) {
    errors.push(`Only one DELIVERY is allowed per order (found ${deliveryCount})`);
  }
  if (deliveryCount === 0) {
    errors.push('Order must have at least one DELIVERY stop');
  }
  const terminateCount = countStopsOfType('TERMINATE');
  if (terminateCount > 1) {
    errors.push(`Only one TERMINATE is allowed per order (found ${terminateCount})`);
  }

  // --- Stop ordering validations ---
  // Parity with legacy leg_movetype / leg_movetype2 (cms.js ~3530-3665):
  // PICKUP must come before DELIVERY, and TERMINATE (when present) must come
  // after DELIVERY. These run on the physical stop sequence, NOT the order
  // pointers — an N-leg chain has N+1 stops: the first leg's origin (legtype)
  // followed by every leg's destination (legAction2). Deriving role positions
  // from the pointers (legtype for PICKUP, legAction2 for DELIVERY/TERMINATE)
  // misses a role that lands on the first leg's legtype — e.g. a TERMINATE
  // origin — which is exactly how a terminate-before-delivery chain slipped
  // through. This is the same stop model countStopsOfType uses above.
  if (legs.length > 0) {
    const stopSequence = [
      String(getEffective(legs[0], 'legtype') || ''),
      ...legs.map((l) => String(getEffective(l, 'legAction2') || '')),
    ];
    const pickupPos = stopSequence.indexOf('PICKUP');
    const deliveryPos = stopSequence.indexOf('DELIVERY');
    const terminatePos = stopSequence.indexOf('TERMINATE');

    if (pickupPos !== -1 && deliveryPos !== -1 && pickupPos > deliveryPos) {
      errors.push('PICKUP stop must be before the DELIVERY stop');
    }
    if (terminatePos !== -1 && deliveryPos !== -1 && terminatePos < deliveryPos) {
      errors.push('TERMINATE stop must be after the DELIVERY stop');
    }
  }

  // --- Chassis split placement (cms.js:5171-5178) ---
  // A chassis split may not sit INSIDE the pickup..delivery or the
  // delivery..terminate span. The pointers are derived from the EFFECTIVE legs,
  // exactly as buildLegSavePayload derives the ones it sends (legSavePayload.ts:
  // 283-285) — the save that breaks this rule is the one that moves the
  // TERMINATE past a split that was legal where it stood, so a stored pointer is
  // the stale one by definition. Pro 5036348: a stop added after the terminate
  // defaulted to CHASSIS SPLIT on leg 4 while the terminate WAS leg 4 (legal —
  // the test is strict inequality), then the terminate moved to 5 and 6 and the
  // split was suddenly inside the span, with every leg already arrived and
  // departed so its type could no longer be changed.
  //
  // V1's strict > and < are deliberate: a split landing exactly ON a pointer is
  // allowed, and terminateleg 0 ("no terminate yet") makes `n < 0` false, so a
  // split after the delivery stays legal until something terminates.
  //
  // Only legAction2 is tested. A chassis split is ONE physical stop, written as
  // leg N's destination and mirrored onto leg N+1's legtype; testing both would
  // report the same stop twice. Same stop model as countStopsOfType above.
  const legnumOfRole = (field: 'legtype' | 'legAction2', role: string): number =>
    legs.find((l) => String(getEffective(l, field) || '') === role)?.legnum ?? 0;
  const originateleg = legnumOfRole('legtype', 'PICKUP');
  const deliveryleg = legnumOfRole('legAction2', 'DELIVERY');
  const terminateleg = legnumOfRole('legAction2', 'TERMINATE');

  legs.forEach((leg) => {
    if (String(getEffective(leg, 'legAction2') || '') !== 'CHASSIS SPLIT') return;
    const n = leg.legnum;
    if (n > originateleg && n < deliveryleg) {
      errors.push(`Leg ${n}: You cannot have a chassis split between the pickup and delivery legs`);
    }
    if (n > deliveryleg && n < terminateleg) {
      errors.push(`Leg ${n}: You cannot have a chassis split between the delivery and terminate legs`);
    }
  });

  return errors;
};
